/*
 * Story mosaic
 * ------------------------------------------------------------
 * Two jobs:
 *
 *  1. Reflow. Liquid prints each tile inside its desktop column so the section
 *     reads correctly without JS. Below the breakpoint the tiles move into the
 *     column their block declares for mobile, sorted by the mobile order. The
 *     move only runs when the breakpoint is actually crossed, so resizing
 *     inside one breakpoint costs nothing.
 *
 *  2. Audio. A text tile with an audio clip plays it on click and stops any
 *     other clip already playing in the same section.
 *
 * Re-runs on shopify:section:load so the theme editor stays in sync.
 */
(function () {
  'use strict';

  var BREAKPOINT = 990;
  var instances = new WeakMap();

  function isDesktop() {
    return window.matchMedia('(min-width: ' + BREAKPOINT + 'px)').matches;
  }

  function StoryMosaic(root) {
    this.root = root;
    this.columns = Array.prototype.slice.call(root.querySelectorAll('.story-mosaic__column'));
    this.tiles = Array.prototype.slice.call(root.querySelectorAll('.story-mosaic__tile'));
    this.audio = null;
    this.playing = null;
    this.layout = null;

    this.onResize = this.apply.bind(this);
    this.onClick = this.handleClick.bind(this);
    this.onKeydown = this.handleKeydown.bind(this);

    this.apply();
    window.addEventListener('resize', this.onResize);
    root.addEventListener('click', this.onClick);
    root.addEventListener('keydown', this.onKeydown);
  }

  StoryMosaic.prototype.apply = function () {
    var desktop = isDesktop();
    var next = desktop ? 'desktop' : 'mobile';
    if (next === this.layout) return;
    this.layout = next;

    var columnKey = desktop ? 'column' : 'columnMobile';
    var orderKey = desktop ? 'order' : 'orderMobile';
    var maxColumns = desktop
      ? parseInt(this.root.dataset.columns, 10) || this.columns.length
      : parseInt(this.root.dataset.columnsMobile, 10) || this.columns.length;

    var byColumn = {};
    this.tiles.forEach(function (tile) {
      var index = parseInt(tile.dataset[columnKey], 10) || 1;
      if (index > maxColumns) index = maxColumns;
      (byColumn[index] = byColumn[index] || []).push(tile);
    });

    Object.keys(byColumn).forEach(function (index) {
      byColumn[index].sort(function (a, b) {
        return (parseInt(a.dataset[orderKey], 10) || 0) - (parseInt(b.dataset[orderKey], 10) || 0);
      });
    });

    this.columns.forEach(function (col) {
      var index = parseInt(col.dataset.column, 10);
      var tiles = index <= maxColumns ? byColumn[index] || [] : [];
      // appendChild moves the node, so re-appending in order both reorders and
      // relocates; a column left without tiles is hidden by `:empty` in CSS.
      tiles.forEach(function (tile) {
        col.appendChild(tile);
      });
    });
  };

  StoryMosaic.prototype.handleClick = function (event) {
    var tile = event.target.closest('[data-audio-src]');
    if (!tile || !this.root.contains(tile)) return;
    this.toggle(tile);
  };

  StoryMosaic.prototype.handleKeydown = function (event) {
    if (event.key !== 'Enter' && event.key !== ' ' && event.key !== 'Spacebar') return;
    var tile = event.target.closest('[data-audio-src]');
    if (!tile || !this.root.contains(tile)) return;
    event.preventDefault();
    this.toggle(tile);
  };

  StoryMosaic.prototype.stop = function () {
    if (!this.audio) return;
    this.audio.pause();
    if (this.playing) {
      this.playing.classList.remove('is-playing');
      this.playing.setAttribute('aria-pressed', 'false');
      this.playing = null;
    }
  };

  StoryMosaic.prototype.toggle = function (tile) {
    var self = this;
    var src = tile.dataset.audioSrc;
    if (!src) return;

    if (this.playing === tile && this.audio && !this.audio.paused) {
      this.stop();
      return;
    }

    this.stop();

    if (!this.audio) {
      this.audio = new Audio();
      this.audio.addEventListener('ended', function () {
        self.stop();
      });
    }

    if (this.audio.getAttribute('src') !== src) this.audio.src = src;
    this.playing = tile;
    tile.classList.add('is-playing');
    tile.setAttribute('aria-pressed', 'true');

    var playback = this.audio.play();
    if (playback && typeof playback.catch === 'function') {
      playback.catch(function () {
        self.stop();
      });
    }
  };

  StoryMosaic.prototype.destroy = function () {
    window.removeEventListener('resize', this.onResize);
    this.root.removeEventListener('click', this.onClick);
    this.root.removeEventListener('keydown', this.onKeydown);
    this.stop();
  };

  function init(scope) {
    var roots = (scope || document).querySelectorAll('[data-story-mosaic]');
    Array.prototype.forEach.call(roots, function (root) {
      var existing = instances.get(root);
      if (existing) existing.destroy();
      instances.set(root, new StoryMosaic(root));
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
    });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    var root = event.target.querySelector('[data-story-mosaic]');
    if (!root) return;
    var instance = instances.get(root);
    if (instance) {
      instance.destroy();
      instances.delete(root);
    }
  });
})();
